import React, { useEffect, useState, useRef } from "react";
import { StaticNote } from "./ui";
import { fetchFinancials, uploadPnl } from "../api";
import "./Dashboard.css";

// Encode an ArrayBuffer to base64 in chunks (avoids call-stack limits).
function toBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

// Accounting format: negatives in parentheses, whole dollars.
const fmtPL = (n) => {
  if (n === null || n === undefined) return "--";
  const s = "$" + Math.abs(Math.round(n)).toLocaleString("en-US");
  return n < 0 ? `(${s})` : s;
};

function PLTable({ title, cols, rows, aThru }) {
  return (
    <section className="panel">
      <div className="panel-head"><h2>{title}</h2></div>
      <div className="pl-scroll">
        <table className="grid pl-table">
          <thead>
            <tr>
              <th>Line Item</th>
              {cols.map((c, i) => (
                <th key={c} className={i <= aThru ? "muted" : i === 12 ? "strong" : ""}>
                  {c}{i <= aThru ? " (A)" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className={r.type === "total" ? "total-row" : r.type === "subtotal" ? "subhead-row" : r.highlight ? "row-highlight" : ""}>
                <td className={"rowhead" + (r.indent ? " indent" : "")}>{r.label}</td>
                {r.vals.map((v, i) => (
                  <td key={i} className={v < 0 ? "neg-text" : ""}>{fmtPL(v)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function PnL({ ctx }) {
  const [fin, setFin] = useState(null);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const load = () => fetchFinancials(ctx).then(setFin).catch((e) => setErr(e.message));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true); setErr(null); setMsg(null);
    try {
      const b64 = toBase64(await file.arrayBuffer());
      const res = await uploadPnl(ctx, b64, file.name);
      setMsg(`Finalized ${res.monthKey} — income $${Math.round(res.totalIncome).toLocaleString()}, net $${Math.round(res.net).toLocaleString()}.`);
      await load();
    } catch (e2) { setErr(e2.message); }
    finally { setUploading(false); }
  };

  if (err && !fin) return <div className="state error">Error: {err}</div>;
  if (!fin) return <div className="state">Loading P&amp;L…</div>;

  const cols = [...fin.monthsAbbr, "Full Year"];
  const p = fin.pnl;
  const row = (label, key, obj, opts = {}) => ({ label, vals: obj[key], ...opts });

  const income = [
    row("Sales - CNA (Facility)", "Sales - CNA (Facility)", p.income, { indent: true }),
    row("Sales - LPN (Facility)", "Sales - LPN (Facility)", p.income, { indent: true }),
    row("Sales - RN (Facility)", "Sales - RN (Facility)", p.income, { indent: true }),
    row("Total Facility Staffing", "Total Facility Staffing", p.income, { type: "subtotal" }),
    row("GC Case Management", "GC Case Management", p.income, { indent: true }),
    row("GC CNA", "GC CNA", p.income, { indent: true }),
    row("GC LPN", "GC LPN", p.income, { indent: true }),
    row("Total GEM Care", "Total GEM Care", p.income, { type: "subtotal" }),
    row("Total Income", "Total Income", p.income, { type: "total" }),
  ];
  const cogs = [
    row("Wages - Facility", "Wages - Facility", p.cogs, { indent: true }),
    row("Wages - GEM", "Wages - GEM", p.cogs, { indent: true }),
    row("Wages - Other (Orientation)", "Wages - Other (Orientation)", p.cogs, { indent: true }),
    row("Total Direct Care Payroll", "Total Direct Care Payroll", p.cogs, { type: "subtotal" }),
    row("Payroll Taxes - Facility", "Payroll Taxes - Facility", p.cogs, { indent: true }),
    row("Payroll Taxes - GEM", "Payroll Taxes - GEM", p.cogs, { indent: true }),
    row("Payroll Taxes - Other", "Payroll Taxes - Other", p.cogs, { indent: true }),
    row("Total Payroll Taxes", "Total Payroll Taxes", p.cogs, { type: "subtotal" }),
    row("Workers' Compensation Insurance", "Workers' Compensation Insurance", p.cogs, { indent: true }),
    row("Total Cost of Goods Sold", "Total Cost of Goods Sold", p.cogs, { type: "total" }),
  ];
  const gross = [{ label: "Gross Profit", vals: p.gross_profit, type: "total", highlight: true }];
  const expenses = [
    row("Meals", "Meals", p.expenses, { indent: true }),
    row("Marketing", "Marketing", p.expenses, { indent: true }),
    row("All Other Expenses", "All Other Expenses", p.expenses, { indent: true }),
    row("Total Expenses (Adjusted)", "Total Expenses (Adjusted)", p.expenses, { type: "total" }),
  ];
  const net = [{ label: "Estimated Net Profitability", vals: p.adj_net_profit, type: "total", highlight: true }];

  const aThru = Number.isInteger(p.actualThroughIndex) ? p.actualThroughIndex : 5;
  const actualLabel = fin.monthsAbbr[aThru] || "";
  const budgetStart = fin.monthsAbbr[aThru + 1] || "";

  return (
    <div className="dash">
      <input ref={fileRef} type="file" accept=".xlsx" onChange={onFile} style={{ display: "none" }} />
      <div className="pl-header">
        <div>
          <h1 className="pl-title">NursePartners Inc. — P&amp;L Budget 2026</h1>
          <p className="pl-sub">Jan–{actualLabel}: Actuals (A) &nbsp;|&nbsp; {budgetStart}–Dec: Budget</p>
        </div>
        <button className="btn btn-refresh" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? "Finalizing…" : "Upload P&L"}
        </button>
      </div>
      {msg && <div className="upload-msg">✓ {msg} The month is now marked Actual.</div>}
      {err && <div className="state error">{err}</div>}
      <PLTable title="Income" cols={cols} rows={income} aThru={aThru} />
      <PLTable title="Cost of Goods Sold" cols={cols} rows={cogs} aThru={aThru} />
      <PLTable title="Gross Profit" cols={cols} rows={gross} aThru={aThru} />
      <PLTable title="Operating Expenses" cols={cols} rows={expenses} aThru={aThru} />
      <PLTable title="Net Profitability" cols={cols} rows={net} aThru={aThru} />
      <StaticNote>{fin.note}</StaticNote>
    </div>
  );
}
